import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Switch,
  InputAdornment
} from '@mui/material';
import { 
  LocalOffer, 
  CalendarToday, 
  Save, 
  Close 
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { useProduct } from '../../context/ProductContext';
import { DiscountType } from '../../models/Discount';

interface DiscountFormProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
  productName: string;
  productPrice: number;
}

const DiscountForm: React.FC<DiscountFormProps> = ({ 
  isOpen, 
  onClose, 
  productId,
  productName,
  productPrice
}) => {
  const { createDiscount } = useProduct();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [discountType, setDiscountType] = useState<DiscountType>(DiscountType.PERCENTAGE);
  const [discountValue, setDiscountValue] = useState<string>('');
  const [isLimited, setIsLimited] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Walidacja i obliczenia
  const discountValueNumber = parseFloat(discountValue);
  const isDiscountValueValid = !isNaN(discountValueNumber) && discountValueNumber > 0;
  const isPercentageValueValid = discountType === DiscountType.PERCENTAGE 
    ? discountValueNumber > 0 && discountValueNumber <= 100
    : true;
  const isFixedValueValid = discountType === DiscountType.PRICE 
    ? discountValueNumber > 0 && discountValueNumber < productPrice
    : true;

  const calculatedPrice = isDiscountValueValid 
    ? discountType === DiscountType.PERCENTAGE 
      ? productPrice * (1 - discountValueNumber / 100)
      : productPrice - discountValueNumber
    : 0;

  const handleDateLimitToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLimited(event.target.checked);
    if (!event.target.checked) {
      setStartDate('');
      setEndDate('');
    } else {
      // Ustaw domyślną datę początkową na dzisiaj
      const today = new Date();
      setStartDate(today.toISOString().split('T')[0]);
      
      // Ustaw domyślną datę końcową na za 30 dni
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 30);
      setEndDate(futureDate.toISOString().split('T')[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isDiscountValueValid || !isPercentageValueValid || !isFixedValueValid) {
      setError('Wprowadź poprawną wartość zniżki');
      return;
    }

    if (isLimited && (!startDate || !endDate)) {
      setError('Wprowadź zakres dat dla ograniczonej czasowo zniżki');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const discountData = {
        type: discountType,
        value: discountValueNumber,
        isLimited,
        startTime: isLimited && startDate ? new Date(startDate) : null,
        endTime: isLimited && endDate ? new Date(endDate) : null
      };

      await createDiscount(productId, discountData);
      
      toast.success('Zniżka została dodana pomyślnie!');
      onClose();
      
      // Odświeżanie strony, aby zobaczyć zmiany
      window.location.reload();
    } catch (err: any) {
      setError(err.message || 'Wystąpił błąd podczas dodawania zniżki');
      toast.error(err.message || 'Wystąpił błąd podczas dodawania zniżki');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setDiscountType(DiscountType.PERCENTAGE);
    setDiscountValue('');
    setIsLimited(false);
    setStartDate('');
    setEndDate('');
    setError(null);
  };

  const handleCloseDialog = () => {
    resetForm();
    onClose();
  };

  // Style dla przycisków (podobne do ReviewSection)
  const buttonVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3, type: 'spring' },
    },
    hover: {
      scale: 1.05,
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.95 },
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      TransitionComponent={motion.div as any}
    >
      <DialogTitle className="bg-gray-50 flex items-center gap-2">
        <LocalOffer className="text-primary" />
        <span>Dodaj zniżkę do produktu: {productName}</span>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent className="pt-5">
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg"
            >
              {error}
            </motion.div>
          )}

          <div className="space-y-6">
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Typ zniżki</FormLabel>
              <RadioGroup
                row
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value as DiscountType)}
              >
                <FormControlLabel 
                  value={DiscountType.PERCENTAGE} 
                  control={<Radio color="primary" />} 
                  label="Procentowa" 
                />
                <FormControlLabel 
                  value={DiscountType.PRICE} 
                  control={<Radio color="primary" />} 
                  label="Kwotowa" 
                />
              </RadioGroup>
            </FormControl>

            <TextField
              label="Wartość zniżki"
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              type="number"
              required
              fullWidth
              error={!isPercentageValueValid || !isFixedValueValid}
              helperText={
                !isPercentageValueValid && discountType === DiscountType.PERCENTAGE
                  ? "Wartość procentowa musi być między 0 a 100"
                  : !isFixedValueValid && discountType === DiscountType.PRICE
                  ? "Wartość zniżki nie może być większa niż cena produktu"
                  : null
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {discountType === DiscountType.PERCENTAGE ? '%' : 'zł'}
                  </InputAdornment>
                ),
              }}
            />

            {isDiscountValueValid && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-3 bg-blue-50 text-blue-700 rounded-lg"
              >
                <h3 className="font-semibold mb-1">Podgląd zniżki:</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div>Cena przed zniżką:</div>
                  <div className="font-medium">{productPrice.toFixed(2)} zł</div>
                  
                  <div>Wartość zniżki:</div>
                  <div className="font-medium">
                    {discountType === DiscountType.PERCENTAGE
                      ? `${discountValue}% (${(productPrice * discountValueNumber / 100).toFixed(2)} zł)`
                      : `${discountValue} zł`}
                  </div>
                  
                  <div>Cena po zniżce:</div>
                  <div className="font-medium text-green-600">{calculatedPrice.toFixed(2)} zł</div>
                </div>
              </motion.div>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={isLimited}
                  onChange={handleDateLimitToggle}
                  color="primary"
                />
              }
              label="Zniżka ograniczona czasowo"
            />

            <AnimatePresence>
              {isLimited && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-hidden"
                >
                  <div className="flex items-center space-x-2">
                    <CalendarToday className="text-gray-500" fontSize="small" />
                    <TextField
                      label="Data początkowa"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required={isLimited}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <CalendarToday className="text-gray-500" fontSize="small" />
                    <TextField
                      label="Data końcowa"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required={isLimited}
                      InputProps={{
                        inputProps: { min: startDate }
                      }}
                      error={Boolean(startDate && endDate && new Date(startDate) > new Date(endDate))}
                      helperText={
                        startDate && endDate && new Date(startDate) > new Date(endDate)
                          ? "Data końcowa musi być późniejsza niż początkowa"
                          : null
                      }
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </DialogContent>

        <div className="p-4 bg-gray-50 flex justify-end space-x-4">
          <motion.button
            type="button"
            onClick={handleCloseDialog}
            disabled={isSubmitting}
            className="inline-flex items-center justify-center rounded-full px-6 py-3 bg-gray-100 text-gray-700 font-medium"
            variants={buttonVariants}
            initial="initial"
            animate="animate"
            whileHover={isSubmitting ? {} : "hover"}
            whileTap={isSubmitting ? {} : "tap"}
          >
            <Close fontSize="small" className="mr-1" />
            <span>Anuluj</span>
          </motion.button>
          
          <motion.button
            type="submit"
            disabled={
              isSubmitting || 
              !isDiscountValueValid || 
              !isPercentageValueValid || 
              !isFixedValueValid ||
              (isLimited && (!startDate || !endDate || new Date(startDate) > new Date(endDate)))
            }
            className={`inline-flex items-center justify-center rounded-full px-6 py-3 font-medium ${
              isSubmitting || 
              !isDiscountValueValid || 
              !isPercentageValueValid || 
              !isFixedValueValid ||
              (isLimited && (!startDate || !endDate || new Date(startDate) > new Date(endDate)))
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white'
            }`}
            variants={buttonVariants}
            initial="initial"
            animate="animate"
            whileHover={
              isSubmitting || 
              !isDiscountValueValid || 
              !isPercentageValueValid || 
              !isFixedValueValid ||
              (isLimited && (!startDate || !endDate || new Date(startDate) > new Date(endDate)))
                ? {} 
                : "hover"
            }
            whileTap={
              isSubmitting || 
              !isDiscountValueValid || 
              !isPercentageValueValid || 
              !isFixedValueValid ||
              (isLimited && (!startDate || !endDate || new Date(startDate) > new Date(endDate)))
                ? {} 
                : "tap"
            }
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <span className="mr-2">Zapisywanie</span>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              </div>
            ) : (
              <>
                <Save fontSize="small" className="mr-1" />
                <span>Zapisz zniżkę</span>
              </>
            )}
          </motion.button>
        </div>
      </form>
    </Dialog>
  );
};

export default DiscountForm;
